/**
 * OMS VAT treatments ↔ Xero purchase TaxType mapping.
 *
 * Architecture (Part 7 — extensible, not fragile if/else):
 *
 * 1. OMS_VAT_TREATMENT_DEFINITIONS — declarative registry. Add a new
 *    legitimate treatment by appending one definition (id, label, aliases,
 *    Xero matchers). No resolver if/else growth.
 * 2. Venue.xeroConnection.vatTaxTypeMap / vatTaxMapping — per-org overrides
 *    of TaxType codes (organisations expose different rates via /TaxRates).
 * 3. resolveXeroTaxMapping — ordered resolution:
 *      org override → match active TaxRates via registry → preferred default
 *    Optional failClosed when rates are loaded but nothing matches.
 *
 * Do not collapse No VAT into Zero-rated — they are separate registry entries.
 */

/** @typedef {'standard'|'zeroRated'|'noVat'|string} OmsVatTreatmentId */

/**
 * @typedef {object} OmsVatTreatmentDefinition
 * @property {string} id
 * @property {string} label
 * @property {boolean} impliesHasVat
 * @property {string[]} [aliases]
 * @property {object} xeroPurchase
 * @property {string[]} xeroPurchase.preferredTaxTypes  first = UK default fallback
 * @property {RegExp[]} [xeroPurchase.namePatterns]
 * @property {string[]} [xeroPurchase.excludeTaxTypes]
 */

/** @type {OmsVatTreatmentDefinition[]} */
export const OMS_VAT_TREATMENT_DEFINITIONS = [
  {
    id: 'standard',
    label: 'Standard-rated',
    impliesHasVat: true,
    aliases: ['standard_rated', 'standardrated', 'input2', 'input', 'vat'],
    xeroPurchase: {
      preferredTaxTypes: ['INPUT2', 'INPUT'],
      namePatterns: [/\b20\s*%/, /vat on expenses/i],
      excludeTaxTypes: [],
    },
  },
  {
    id: 'zeroRated',
    label: 'Zero-rated',
    impliesHasVat: false,
    aliases: ['zero_rated', 'zero-rated', 'zeroratedinput'],
    xeroPurchase: {
      preferredTaxTypes: ['ZERORATEDINPUT'],
      namePatterns: [/zero\s*rated/i],
      // Never confuse with outside-scope / No VAT
      excludeTaxTypes: ['NONE'],
    },
  },
  {
    id: 'noVat',
    label: 'No VAT',
    impliesHasVat: false,
    aliases: ['no_vat', 'no-vat', 'none', 'outside_scope', 'outsidescope'],
    xeroPurchase: {
      preferredTaxTypes: ['NONE'],
      namePatterns: [/^no\s*vat$/i, /outside\s*the\s*scope/i, /\bno\s*vat\b/i],
      // Never fall through to zero-rated
      excludeTaxTypes: ['ZERORATEDINPUT'],
    },
  },
  // Future example (enable by uncommenting / appending):
  // {
  //   id: 'reduced',
  //   label: 'Reduced-rated (5%)',
  //   impliesHasVat: true,
  //   aliases: ['reduced_rated', 'rrinput', '5percent'],
  //   xeroPurchase: {
  //     preferredTaxTypes: ['RRINPUT'],
  //     namePatterns: [/\b5\s*%/, /reduced/i],
  //     excludeTaxTypes: [],
  //   },
  // },
]

/** Convenience id constants — keep in sync with OMS_VAT_TREATMENT_DEFINITIONS. */
export const OMS_VAT_TREATMENT = Object.freeze({
  STANDARD: 'standard',
  ZERO_RATED: 'zeroRated',
  NO_VAT: 'noVat',
})

export const OMS_VAT_TREATMENT_LABELS = Object.freeze(
  Object.fromEntries(
    OMS_VAT_TREATMENT_DEFINITIONS.map(def => [def.id, def.label]),
  ),
)

/** Default UK purchase TaxType = first preferredTaxTypes entry per definition. */
export const DEFAULT_XERO_PURCHASE_TAX_TYPE = Object.freeze(
  Object.fromEntries(
    OMS_VAT_TREATMENT_DEFINITIONS.map(def => [
      def.id,
      def.xeroPurchase.preferredTaxTypes[0],
    ]),
  ),
)

function buildAliasIndex(definitions = OMS_VAT_TREATMENT_DEFINITIONS) {
  const map = new Map()
  for (const def of definitions) {
    map.set(def.id.toLowerCase(), def)
    for (const alias of def.aliases || []) {
      map.set(String(alias).toLowerCase().replace(/\s+/g, '_'), def)
    }
  }
  return map
}

const ALIAS_INDEX = buildAliasIndex()

export function getOmsVatTreatmentDefinition(treatmentId) {
  if (treatmentId == null || treatmentId === '') return null
  const key = String(treatmentId).trim().toLowerCase().replace(/\s+/g, '_')
  return ALIAS_INDEX.get(key) || null
}

export function listOmsVatTreatmentOptions(
  definitions = OMS_VAT_TREATMENT_DEFINITIONS,
) {
  return definitions.map(def => ({
    value: def.id,
    label: def.label,
    impliesHasVat: Boolean(def.impliesHasVat),
  }))
}

export function normaliseOmsVatTreatment(value) {
  const def = getOmsVatTreatmentDefinition(value)
  return def ? def.id : null
}

/**
 * Derive OMS VAT treatment from persisted fields.
 * Legacy: hasVat true → standard. Order.hasVat defaults to false, so bare
 * `false` must NOT block product.hasVat (Belhaven / Smart Match VAT bug —
 * keep in sync with express_app/utils/vatTreatment.js).
 */
export function resolveOmsVatTreatment({ vatTreatment, hasVat, product } = {}) {
  const explicit = normaliseOmsVatTreatment(vatTreatment)
  if (explicit) return explicit

  const productExplicit = normaliseOmsVatTreatment(product?.vatTreatment)
  if (productExplicit) return productExplicit

  if (hasVat === true || product?.hasVat === true) {
    return OMS_VAT_TREATMENT.STANDARD
  }

  return OMS_VAT_TREATMENT.ZERO_RATED
}

export function omsVatTreatmentImpliesHasVat(treatment) {
  const def = getOmsVatTreatmentDefinition(treatment)
  if (def) return Boolean(def.impliesHasVat)
  return normaliseOmsVatTreatment(treatment) === OMS_VAT_TREATMENT.STANDARD
}

export function buildVatFieldsForPersist({
  vatTreatment,
  hasVat,
  priceIncludesVat = false,
} = {}) {
  const treatment =
    normaliseOmsVatTreatment(vatTreatment) ||
    (hasVat === true
      ? OMS_VAT_TREATMENT.STANDARD
      : OMS_VAT_TREATMENT.ZERO_RATED)

  const nextHasVat = omsVatTreatmentImpliesHasVat(treatment)
  return {
    vatTreatment: treatment,
    hasVat: nextHasVat,
    priceIncludesVat: nextHasVat ? Boolean(priceIncludesVat) : false,
  }
}

/**
 * Read mapping overrides from venue.xeroConnection (supports both shapes).
 * @returns {{ taxTypeByTreatment: Object, failClosed: boolean }}
 */
export function readVenueVatTaxMapping(xeroConnection = {}) {
  const nested = xeroConnection?.vatTaxMapping
  const flat = xeroConnection?.vatTaxTypeMap
  const taxTypeByTreatment = {
    ...(flat && typeof flat === 'object' ? flat : {}),
    ...(nested?.taxTypeByTreatment &&
    typeof nested.taxTypeByTreatment === 'object'
      ? nested.taxTypeByTreatment
      : {}),
  }
  return {
    taxTypeByTreatment,
    failClosed: Boolean(
      nested?.failClosed ?? xeroConnection?.vatTaxMappingFailClosed,
    ),
  }
}

function isActiveTaxRate(rate) {
  const status = String(rate?.Status || 'ACTIVE').toUpperCase()
  return status === 'ACTIVE'
}

function rateExcluded(rate, excludeTaxTypes = []) {
  const type = String(rate?.TaxType || '').toUpperCase()
  return (excludeTaxTypes || []).some(
    excluded => String(excluded).toUpperCase() === type,
  )
}

/**
 * Match a registry definition against org TaxRates (table-driven, no if/else).
 */
export function matchTaxRateForTreatment(definition, xeroTaxRates = []) {
  if (!definition?.xeroPurchase) return null
  const {
    preferredTaxTypes = [],
    namePatterns = [],
    excludeTaxTypes = [],
  } = definition.xeroPurchase

  const active = (Array.isArray(xeroTaxRates) ? xeroTaxRates : []).filter(
    isActiveTaxRate,
  )

  for (const preferred of preferredTaxTypes) {
    const hit = active.find(
      rate =>
        String(rate?.TaxType || '').toUpperCase() ===
          String(preferred).toUpperCase() &&
        !rateExcluded(rate, excludeTaxTypes),
    )
    if (hit?.TaxType) return hit
  }

  for (const pattern of namePatterns) {
    const hit = active.find(rate => {
      if (rateExcluded(rate, excludeTaxTypes)) return false
      const name = String(rate?.Name || '')
      try {
        return pattern.test(name)
      } catch (_e) {
        return false
      }
    })
    if (hit?.TaxType) return hit
  }

  return null
}

export const XERO_TAX_MAPPING_SOURCE = {
  ORG_OVERRIDE: 'org_override',
  TAX_RATES_MATCH: 'tax_rates_match',
  REGISTRY_DEFAULT: 'registry_default',
  UNKNOWN_TREATMENT_FALLBACK: 'unknown_treatment_fallback',
}

/**
 * Full mapping result for logging / fail-closed callers.
 *
 * @returns {{
 *   taxType: string,
 *   treatment: string,
 *   source: string,
 *   error: string|null,
 * }}
 */
export function resolveXeroTaxMapping({
  treatment,
  xeroTaxRates = [],
  /** @deprecated prefer mappingConfig / xeroConnection */
  overrideMap = null,
  mappingConfig = null,
  xeroConnection = null,
  failClosed = null,
} = {}) {
  const fromVenue = xeroConnection
    ? readVenueVatTaxMapping(xeroConnection)
    : { taxTypeByTreatment: {}, failClosed: false }
  const taxTypeByTreatment = {
    ...fromVenue.taxTypeByTreatment,
    ...(mappingConfig?.taxTypeByTreatment || {}),
    ...(overrideMap && typeof overrideMap === 'object' ? overrideMap : {}),
  }
  const closed =
    failClosed != null
      ? Boolean(failClosed)
      : Boolean(mappingConfig?.failClosed ?? fromVenue.failClosed)

  const def = getOmsVatTreatmentDefinition(treatment)
  const treatmentId = def?.id || normaliseOmsVatTreatment(treatment)

  // 1) Explicit org / venue override for this treatment id (or raw key)
  const override =
    (treatmentId && taxTypeByTreatment[treatmentId]) ||
    taxTypeByTreatment[treatment]
  if (override && String(override).trim()) {
    return {
      taxType: String(override).trim(),
      treatment: treatmentId || String(treatment || ''),
      source: XERO_TAX_MAPPING_SOURCE.ORG_OVERRIDE,
      error: null,
    }
  }

  // 2) Registry definition + live TaxRates
  if (def) {
    const matched = matchTaxRateForTreatment(def, xeroTaxRates)
    if (matched?.TaxType) {
      return {
        taxType: matched.TaxType,
        treatment: def.id,
        source: XERO_TAX_MAPPING_SOURCE.TAX_RATES_MATCH,
        error: null,
      }
    }

    const fallback = def.xeroPurchase.preferredTaxTypes[0]
    const ratesLoaded = Array.isArray(xeroTaxRates) && xeroTaxRates.length > 0
    if (closed && ratesLoaded) {
      const err = new Error(
        `No Xero tax rate mapped for OMS VAT treatment "${def.id}" (${def.label}). Configure venue.xeroConnection.vatTaxTypeMap or ensure the organisation has a matching TaxRate.`,
      )
      err.code = 'XERO_VAT_TAX_TYPE_UNMAPPED'
      err.treatment = def.id
      throw err
    }

    return {
      taxType: fallback,
      treatment: def.id,
      source: XERO_TAX_MAPPING_SOURCE.REGISTRY_DEFAULT,
      error: ratesLoaded
        ? `No TaxRates match for "${def.id}"; using registry default ${fallback}`
        : null,
    }
  }

  // 3) Unknown treatment — never invent a code from VAT % if/else.
  // Prefer org override already checked; else fall back to zero-rated default
  // only when no override exists (safe legacy), or fail closed.
  const unknownFallback =
    DEFAULT_XERO_PURCHASE_TAX_TYPE[OMS_VAT_TREATMENT.ZERO_RATED]
  if (closed) {
    const err = new Error(
      `Unknown OMS VAT treatment "${treatment}". Register it in OMS_VAT_TREATMENT_DEFINITIONS or map it in vatTaxTypeMap.`,
    )
    err.code = 'XERO_VAT_TREATMENT_UNKNOWN'
    throw err
  }

  return {
    taxType: unknownFallback,
    treatment: String(treatment || OMS_VAT_TREATMENT.ZERO_RATED),
    source: XERO_TAX_MAPPING_SOURCE.UNKNOWN_TREATMENT_FALLBACK,
    error: `Unknown treatment "${treatment}"; using ${unknownFallback}`,
  }
}

/**
 * Convenience: TaxType string only (existing call sites).
 */
export function resolveXeroPurchaseTaxType(
  treatment,
  xeroTaxRates = [],
  overrideMap = null,
  options = {},
) {
  const result = resolveXeroTaxMapping({
    treatment,
    xeroTaxRates,
    overrideMap,
    xeroConnection: options.xeroConnection,
    mappingConfig: options.mappingConfig,
    failClosed: options.failClosed,
  })
  return result.taxType
}

export function resolveLineVatTreatment(line = {}) {
  return resolveOmsVatTreatment({
    vatTreatment: line?.vatTreatment,
    hasVat: line?.hasVat,
    product: line?.product,
  })
}

export function resolveFinanceDocumentVatTreatment(document = {}) {
  const explicit = normaliseOmsVatTreatment(document?.vatTreatment)
  if (explicit) return explicit

  const vatPennies = Number(document?.invoiceVatTotal)
  if (Number.isFinite(vatPennies) && Math.abs(vatPennies) > 0) {
    return OMS_VAT_TREATMENT.STANDARD
  }

  return OMS_VAT_TREATMENT.ZERO_RATED
}
